import React, { useState } from 'react';
import { Pressable, Platform } from 'react-native';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MoreVerticalIcon } from '@hugeicons-pro/core-solid-rounded';
import {
  PencilEdit02Icon,
  Delete02Icon,
  Alert02Icon,
  FolderLockedIcon,
  UserAccountIcon,
  Monocle01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { ContextSheet } from '@/components/ui/ContextSheet';
import EditPhotoCaption from '@/components/domain/photos/EditPhotoCaption';
import { AlertDialog, AlertDialogBackdrop, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader } from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { HStack } from '@/components/gluestack/hstack';
import { Box } from '@/components/gluestack/box';
import { Photo } from '@/models/Photo';
import { MemberDetails } from '@/models/MemberDetails';
import { PhotosService } from '@/services/domain/PhotosService';
import { useSupportService } from '@/hooks/useSupportService';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import { useAuth } from '@/components/providers/AuthProvider';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { plainToClass } from '@/models/transform';

interface IPhotoContextMenuProps {
  photo: Photo;
  onCloseViewer: () => void;
  onBottomSheetOpenChange?: (open: boolean) => void;
}

export function PhotoContextMenu({ photo, onCloseViewer, onBottomSheetOpenChange }: IPhotoContextMenuProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { submitPolicyViolationForPhoto } = useSupportService();
  const queryClient = useQueryClient();
  const { removeCacheItem, updateSingleQuery } = useQueryCacheUtils();
  const { accountStatus, updateAccountIconUrl } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showEditCaptionSheet, setShowEditCaptionSheet] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);

  const handleReport = () => {
    if (!photo.Guid || !photo.Member) return;
    setShowReportReasonSheet(true);
    onBottomSheetOpenChange?.(true);
  };

  const handleDelete = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PhotosService.deletePhoto(photo.Id);
      showSuccessToast(t('photo.delete.success'), undefined, 'bottom');
      // Remove photo from all cached photo lists (latest, network, member folders, etc.)
      removeCacheItem('photos', photo.Id, 'PhotosItems');
      queryClient.invalidateQueries({ queryKey: ['photos', 'member-preview'] });
      queryClient.removeQueries({ queryKey: ['photo', photo.Id], exact: true });
      if (photo.Guid) {
        queryClient.removeQueries({ queryKey: ['photo', photo.Guid], exact: true });
      }
      // Update member details counts
      if (photo.Member?.UserName) {
        const countField = photo.IsPublic ? 'PublicPhotosCount' : 'PrivatePhotosCount';
        queryClient.setQueryData(['member-details', photo.Member.UserName], (oldData: MemberDetails | undefined) => {
          if (!oldData) return oldData;
          const currentCount = oldData[countField] ?? 0;
          return plainToClass(MemberDetails, {
            ...oldData,
            [countField]: Math.max(0, currentCount - 1),
          });
        });
      }
      onCloseViewer();
    } catch (error) {
      showErrorToast(t('photo.delete.error'), error);
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleSetProfile = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PhotosService.setPhotoAsProfile(photo.Id);
      showSuccessToast(t('photo.profile.setSuccess'));
      // Optimistically update caches so UI reflects immediately while viewer is open
      // 1) Update the member-details cache (if present) with new profile photo
      if (photo.Member?.UserName) {
        const userName = photo.Member.UserName;
        queryClient.setQueryData(['member-details', userName], (oldData: MemberDetails | undefined) => {
          if (!oldData) return oldData;
          return plainToClass(MemberDetails, {
            ...oldData,
            // MemberDetails extends Member, so keep IconUrl in sync too
            IconUrl: photo.IconUrl,
            PhotoUrl: photo.FullSizeUrl,
            PhotoId: photo.Id,
          });
        });
      }

      // 2) Update the individual photo detail cache so the header avatar (photo.Member.IconUrl) updates
      // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
      queryClient.setQueriesData({ queryKey: ['photo', photo.Id] }, (oldPhoto: Photo | undefined) => {
        if (!oldPhoto) return oldPhoto;
        return plainToClass(Photo, {
          ...oldPhoto,
          Member: { ...oldPhoto.Member, IconUrl: photo.IconUrl },
        });
      });
      if (photo.Guid) {
        queryClient.setQueriesData({ queryKey: ['photo', photo.Guid] }, (oldPhoto: Photo | undefined) => {
          if (!oldPhoto) return oldPhoto;
          return plainToClass(Photo, {
            ...oldPhoto,
            Member: { ...oldPhoto.Member, IconUrl: photo.IconUrl },
          });
        });
      }

      // Remove member-preview photo cache so ProfilePhotosPreview fetches fresh data
      // (invalidation alone shows stale cached data since the query has no active observer while on the Photos tab)
      queryClient.removeQueries({ queryKey: ['photos', 'member-preview'] });

      // Finally, invalidate related queries to fetch fresh data where enabled
      if (photo.Member?.UserName) {
        queryClient.invalidateQueries({ queryKey: ['member-details', photo.Member.UserName], exact: false });
      }

      // 3) If this is the signed-in user, update AuthProvider accountStatus IconUrl
      if (accountStatus && accountStatus.MemberId === photo.Member?.Id) {
        updateAccountIconUrl(photo.IconUrl);
      }
    } catch (error) {
      showErrorToast(t('photo.profile.setError'), error);
    } finally {
      setIsLoading(false);
      setShowProfileDialog(false);
    }
  };

  const handleMoveToPrivate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PhotosService.setPhotoAsPrivate(photo.Id);
      showSuccessToast(t('photo.private.success'));
      // Update caches: remove the photo from public lists and from the member's public folder
      removeCacheItem('photos', photo.Id, 'PhotosItems');
      // Invalidate photo list queries
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      // Also update individual photo detail caches so stale IsPublic does not persist while viewer is open
      // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
      queryClient.setQueriesData({ queryKey: ['photo', photo.Id] }, (oldData: Photo | undefined) => {
        if (!oldData) return oldData;
        return plainToClass(Photo, { ...oldData, IsPublic: false, Rating: 'private' });
      });
      if (photo.Guid) {
        queryClient.setQueriesData({ queryKey: ['photo', photo.Guid] }, (oldData: Photo | undefined) => {
          if (!oldData) return oldData;
          return plainToClass(Photo, { ...oldData, IsPublic: false, Rating: 'private' });
        });
      }
      // Update member details counts: decrement public, increment private
      if (photo.Member?.UserName) {
        queryClient.setQueryData(['member-details', photo.Member.UserName], (oldData: MemberDetails | undefined) => {
          if (!oldData) return oldData;
          const currentPublicCount = oldData.PublicPhotosCount ?? 0;
          const currentPrivateCount = oldData.PrivatePhotosCount ?? 0;
          return plainToClass(MemberDetails, {
            ...oldData,
            PublicPhotosCount: Math.max(0, currentPublicCount - 1),
            PrivatePhotosCount: currentPrivateCount + 1,
          });
        });
      }
    } catch (error) {
      showErrorToast(t('photo.private.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReview = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PhotosService.requestPhotoReview(photo.Id);
      showSuccessToast(t('photo.review.requestSuccess'));
      // Optimistically update the currently viewed photo so the badge reflects immediately
      // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
      queryClient.setQueriesData({ queryKey: ['photo', photo.Id] }, (oldPhoto: Photo | undefined) => {
        if (!oldPhoto) return oldPhoto;
        return plainToClass(Photo, { ...oldPhoto, Review: 'queued', EligibleForReview: false });
      });
      if (photo.Guid) {
        queryClient.setQueriesData({ queryKey: ['photo', photo.Guid] }, (oldPhoto: Photo | undefined) => {
          if (!oldPhoto) return oldPhoto;
          return plainToClass(Photo, { ...oldPhoto, Review: 'queued', EligibleForReview: false });
        });
      }
      setShowReviewDialog(false);
    } catch (error) {
      showErrorToast(t('photo.review.requestError'), error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <Menu
        placement="bottom right"
        offset={10}
        trigger={(props: any) => (
          <Pressable
            {...props}
            className="self-start h-10 w-10 items-center justify-center rounded-full bg-white/20"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <HugeIconsWrapper icon={MoreVerticalIcon} size={22} className="text-white" />
          </Pressable>
        )}
      >
        {photo.IsOwner && (
          <MenuItem key="edit" textValue={t('photo.menu.editCaption')} onPress={() => { setShowEditCaptionSheet(true); onBottomSheetOpenChange?.(true); }}>
            <MenuItemIcon icon={PencilEdit02Icon} />
            <MenuItemLabel>{t('photo.menu.editCaption')}</MenuItemLabel>
          </MenuItem>
        )}
        {photo.IsOwner && photo.IsPublic && (
          <MenuItem key="profile" textValue={t('photo.menu.useAsProfilePhoto')} onPress={() => setShowProfileDialog(true)} disabled={isLoading}>
            <MenuItemIcon icon={UserAccountIcon} />
            <MenuItemLabel>{t('photo.menu.useAsProfilePhoto')}</MenuItemLabel>
          </MenuItem>
        )}
        {photo.IsOwner && photo.IsPublic && !photo.Member?.IsEvent && (
          <MenuItem key="private" textValue={t('photo.menu.moveToPrivate')} onPress={handleMoveToPrivate} disabled={isLoading}>
            <MenuItemIcon icon={FolderLockedIcon} />
            <MenuItemLabel>{t('photo.menu.moveToPrivate')}</MenuItemLabel>
          </MenuItem>
        )}
        {photo.EligibleForReview && (
          <MenuItem key="review" textValue={t('photo.menu.requestReview')} onPress={() => setShowReviewDialog(true)} disabled={isLoading}>
            <MenuItemIcon icon={Monocle01Icon} />
            <MenuItemLabel>{t('photo.menu.requestReview')}</MenuItemLabel>
          </MenuItem>
        )}
        {!photo.IsOwner && (
          <MenuItem key="report" textValue={t('photo.menu.report')} onPress={handleReport}>
            <MenuItemIcon icon={Alert02Icon} className="color-error-500" />
            <MenuItemLabel>{t('photo.menu.report')}</MenuItemLabel>
          </MenuItem>
        )}
        {(photo.IsOwner || (Platform.OS === 'web' && photo.CanModerate)) && (
          <MenuItem key="delete" textValue={t('photo.menu.delete')} onPress={() => setShowDeleteDialog(true)} disabled={isLoading}>
            <MenuItemIcon icon={Delete02Icon} className="color-error-500" />
            <MenuItemLabel>{t('photo.menu.delete')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>

      <ContextSheet
        isOpen={showEditCaptionSheet}
        onClose={() => { setShowEditCaptionSheet(false); onBottomSheetOpenChange?.(false); }}
        onWillClose={() => onBottomSheetOpenChange?.(false)}
        contentComponent={EditPhotoCaption}
        title={t('photo.menu.editCaption')}
        payload={{ photo }}
      />

      <AlertDialog isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('photo.delete.confirmTitle')}</Heading>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('photo.delete.confirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setShowDeleteDialog(false)} disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleDelete} disabled={isLoading}>
              <ButtonText>{t('common.delete')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog isOpen={showProfileDialog} onClose={() => setShowProfileDialog(false)} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('photo.profile.confirmTitle')}</Heading>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('photo.profile.confirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setShowProfileDialog(false)} disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="primary" onPress={handleSetProfile} disabled={isLoading}>
              <ButtonText>{t('common.continue')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog isOpen={showReviewDialog} onClose={() => setShowReviewDialog(false)} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('photo.review.dialogTitle')}</Heading>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('photo.review.dialogIntro')}</Text>
            <Text className="mt-4 font-semibold">{t('photo.review.possibleRatingsTitle')}</Text>
            {(['public', 'public-sensitive', 'private'] as const).map((ratingKey) => (
              <HStack key={ratingKey} className="mt-3 items-baseline gap-2">
                <Badge size="md" action="muted">
                  <BadgeText>{t(`PhotoRating.${ratingKey}.Text`, { ns: 'domain' })}</BadgeText>
                </Badge>
                <Box className="flex-1">
                  <Text size="sm">
                    {t(`PhotoRating.${ratingKey}.Description`, { ns: 'domain' })}
                  </Text>
                </Box>
              </HStack>
            ))}
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setShowReviewDialog(false)} disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="primary" onPress={handleRequestReview} disabled={isLoading}>
              <ButtonText>{t('photo.menu.requestReview')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReportReasonSheet
        isOpen={showReportReasonSheet}
        onClose={() => { setShowReportReasonSheet(false); onBottomSheetOpenChange?.(false); }}
        subject="photo"
        onReasonSelected={(reason) => submitPolicyViolationForPhoto(photo, reason)}
      />
    </>
  );
}

export default PhotoContextMenu;


